// components/user/lead/calculator/FaultCategory.jsx

import { Card, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";

const { Text } = Typography;

const FaultCategory = ({
  category,
  selectedFaults,
  selectedOptions,
  onToggleFault,
  onToggleOption,
  isDarkMode,
}) => {
  // Return null if no faults
  if (!category.faults || category.faults.length === 0) {
    return null;
  }

  const selectionColor = category.selection_color || "#22c55e";

  const handleFaultToggle = (faultId) => {
    onToggleFault(faultId, category);
  };

  const handleOptionSelect = (faultId, optionId) => {
    onToggleOption({ faultId, optionId });
  };

  const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";
  const textColor = isDarkMode ? "text-white" : "text-gray-800";

  // Separate faults with and without options
  const regularFaults =
    category.faults?.filter(
      (fault) => !fault.options || fault.options.length === 0,
    ) || [];

  const optionFaults =
    category.faults?.filter(
      (fault) => fault.options && fault.options.length > 0,
    ) || [];

  // When a fault with options is toggled
  const handleFaultToggleWithOptions = (faultId) => {
    onToggleFault(faultId, category);
  };

  return (
    <Card
      className={`${cardBg} shadow-sm rounded-lg border-0`}
      bordered={false}
      size="small"
      styles={{
        body: { padding: "16px" },
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Text strong className={`text-base ${textColor}`}>
          {category.name}
        </Text>
      </div>

      {/* Regular Faults Grid */}
      {regularFaults.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {regularFaults.map((fault) => {
            const isSelected = selectedFaults[fault.id];

            return (
              <div
                key={fault.id}
                onClick={() => handleFaultToggle(fault.id)}
                className={`
                  relative p-4 rounded-lg transition-colors cursor-pointer
                  ${
                    isSelected
                      ? "border-2"
                      : isDarkMode
                        ? "border border-gray-600 bg-gray-700 hover:bg-gray-600"
                        : "border border-gray-200 bg-gray-50 hover:bg-gray-100"
                  }
                `}
                style={isSelected ? { borderColor: selectionColor } : {}}
              >
                <div className="flex flex-col items-center gap-2 text-center">
                  {fault.image ? (
                    <img
                      src={fault.image}
                      alt={fault.name}
                      className="object-contain w-16 h-24"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "";
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center w-12 h-16">
                      <div
                        className={`w-8 h-8 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                      />
                    </div>
                  )}
                  <Text
                    className={`text-xs font-medium leading-tight ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {fault.name}
                  </Text>
                </div>

                {isSelected && (
                  <div
                    className="absolute flex items-center justify-center w-6 h-6 rounded-full shadow-sm -top-2 -right-2"
                    style={{ backgroundColor: selectionColor }}
                  >
                    <CheckOutlined className="text-xs text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Option Faults - Fixed overflow issues */}
      {optionFaults.length > 0 && (
        <div
          className={`space-y-2 ${regularFaults.length > 0 ? "mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" : ""}`}
        >
          {optionFaults.map((fault) => {
            const isSelected = selectedFaults[fault.id] || false;
            const selectedOptionId = selectedOptions[fault.id];

            return (
              <div
                key={fault.id}
                className={`
                  w-full rounded-lg transition-all duration-200 p-3
                  ${isDarkMode ? "bg-gray-700/50 hover:bg-gray-700" : "bg-gray-50 hover:bg-gray-100"}
                `}
              >
                {/* Fault container with proper overflow handling */}
                <div className="flex items-start min-w-0 gap-3">
                  {/* Small Image - flex-shrink-0 prevents shrinking */}
                  {fault.image ? (
                    <img
                      src={fault.image}
                      alt={fault.name}
                      className="flex-shrink-0 object-contain w-10 rounded h-14"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "";
                      }}
                    />
                  ) : (
                    <div className="flex-shrink-0 w-10 h-14">
                      <div
                        className={`w-8 h-8 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                      />
                    </div>
                  )}

                  {/* Fault Name - with truncation for long names */}
                  <div
                    className="flex-shrink-0 cursor-pointer min-w-[60px]"
                    onClick={() => handleFaultToggleWithOptions(fault.id)}
                  >
                    <Text
                      className={`text-sm font-medium block truncate max-w-[120px] ${
                        isSelected
                          ? "text-gray-900 dark:text-white"
                          : isDarkMode
                            ? "text-gray-300"
                            : "text-gray-700"
                      }`}
                      title={fault.name} // Tooltip for truncated text
                    >
                      {fault.name}
                    </Text>
                  </div>

                  {/* Options - with flex-wrap and proper overflow handling */}
                  <div className="flex-1 flex items-center justify-end gap-1.5 flex-wrap min-w-0">
                    {fault.options.map((option) => {
                      const isOptionSelected = selectedOptionId === option.id;

                      return (
                        <button
                          key={option.id}
                          onClick={() =>
                            handleOptionSelect(fault.id, option.id)
                          }
                          className={`
                            px-3 py-1 text-xs rounded-md transition-all duration-200 whitespace-nowrap
                            ${
                              isOptionSelected
                                ? `bg-green-500 text-white shadow-sm`
                                : isDarkMode
                                  ? `bg-gray-600 text-gray-200 hover:bg-gray-500`
                                  : `bg-white text-gray-700 hover:bg-gray-50 border border-gray-300`
                            }
                          `}
                        >
                          <div className="flex items-center gap-1.5">
                            <span>{option.name}</span>
                            {isOptionSelected && (
                              <span className="text-[10px] text-white">✓</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default FaultCategory;
